import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Avatar from '../common/Avatar';
import { NotificationBell } from '../notifications';
import {
  LayoutDashboard,
  Users,
  ClipboardCheck,
  ClipboardList,
  Bell,
  Tag,
  BarChart3,
  Activity,
  UserX,
  Shield,
  FileText,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Sun,
  Moon,
  Megaphone,
  Gift,
  Flag,
  TrendingUp,
  Server,
  Database,
  Ticket,
  Webhook,
  CreditCard,
  Sparkles,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  path: string;
  badge?: number | string;
  badgeColor?: 'red' | 'yellow' | 'blue' | 'green';
}

interface NavSection {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  items: NavItem[];
}

const AdminLayout: React.FC = () => {
  const { state, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'overview',
    'users',
    'platform',
    'analytics',
    'system',
  ]);

  // Helper function to check if user has Admin role (handles both string and array formats)
  const isAdmin = (): boolean => {
    if (!state.user?.role) return false;
    const userRole = state.user.role;
    if (Array.isArray(userRole)) {
      return userRole.includes('Admin' as const);
    }
    return userRole === 'Admin';
  };

  // Redirect non-admin users
  if (!isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You don't have permission to access the admin area.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const navigationSections: NavSection[] = [
    {
      id: 'overview',
      title: 'Overview',
      icon: LayoutDashboard,
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: LayoutDashboard,
          path: '/admin/dashboard',
        },
      ],
    },
    {
      id: 'users',
      title: 'User Management',
      icon: Users,
      items: [
        {
          id: 'users',
          label: 'All Users',
          icon: Users,
          path: '/admin/users',
        },
        {
          id: 'approvals',
          label: 'Barbershop Approvals',
          icon: ClipboardCheck,
          path: '/admin/approvals',
          badge: 'New',
          badgeColor: 'yellow',
        },
        {
          id: 'approvals-enhanced',
          label: 'Enhanced Approvals',
          icon: ClipboardList,
          path: '/admin/approvals/enhanced',
        },
      ],
    },
    {
      id: 'platform',
      title: 'Platform Operations',
      icon: Server,
      items: [
        {
          id: 'notifications',
          label: 'Notifications',
          icon: Bell,
          path: '/admin/notifications',
        },
        {
          id: 'promo-codes',
          label: 'Promo Codes',
          icon: Tag,
          path: '/admin/promo-codes',
        },
        {
          id: 'campaigns',
          label: 'Marketing Campaigns',
          icon: Megaphone,
          path: '/admin/campaigns',
        },
        {
          id: 'win-back',
          label: 'Win-Back Offers',
          icon: Gift,
          path: '/admin/win-back-offers',
        },
        {
          id: 'invite-codes',
          label: 'Invite Codes',
          icon: Ticket,
          path: '/admin/invite-codes',
        },
        {
          id: 'reviews',
          label: 'Review Moderation',
          icon: Flag,
          path: '/admin/reviews',
        },
      ],
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      icon: BarChart3,
      items: [
        {
          id: 'analytics',
          label: 'Platform Analytics',
          icon: TrendingUp,
          path: '/admin/analytics',
        },
        {
          id: 'usage',
          label: 'Usage Analytics',
          icon: Activity,
          path: '/admin/usage',
        },
        {
          id: 'no-shows',
          label: 'No-Show Management',
          icon: UserX,
          path: '/admin/no-shows',
        },
        {
          id: 'gift-cards',
          label: 'Gift Cards',
          icon: CreditCard,
          path: '/admin/gift-cards',
        },
      ],
    },
    {
      id: 'system',
      title: 'System & Security',
      icon: Shield,
      items: [
        {
          id: 'audit',
          label: 'Audit Logs',
          icon: FileText,
          path: '/admin/audit',
        },
        {
          id: 'support',
          label: 'Support Tickets',
          icon: HelpCircle,
          path: '/admin/support',
        },
        {
          id: 'system-health',
          label: 'System Health',
          icon: Database,
          path: '/admin/system-health',
        },
        {
          id: 'webhook-events',
          label: 'Webhook Events',
          icon: Webhook,
          path: '/admin/webhook-events',
        },
        {
          id: 'demo',
          label: 'Demo Data',
          icon: Sparkles,
          path: '/admin/demo',
        },
      ],
    },
  ];

  const isActive = (path: string) => {
    return location.pathname === path ||
           (path !== '/admin/dashboard' && location.pathname.startsWith(path));
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getBadgeClasses = (color?: 'red' | 'yellow' | 'blue' | 'green') => {
    switch (color) {
      case 'red':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'blue':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'green':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400';
    }
  };

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    return (
      <Link
        key={item.id}
        to={item.path}
        onClick={() => setIsMobileOpen(false)}
        className={`
          flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
          transition-all duration-200 group
          ${active
            ? 'bg-primary-600 text-white shadow-md'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
          }
        `}
        title={isCollapsed ? item.label : undefined}
      >
        <Icon
          size={18}
          className={`flex-shrink-0 ${active ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'}`}
        />
        {!isCollapsed && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {item.badge && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getBadgeClasses(item.badgeColor)}`}>
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
    );
  };

  const renderSection = (section: NavSection) => {
    const isExpanded = expandedSections.includes(section.id);
    const SectionIcon = section.icon;

    return (
      <div key={section.id} className="mb-2">
        {!isCollapsed && (
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <div className="flex items-center gap-2">
              <SectionIcon size={14} />
              <span>{section.title}</span>
            </div>
            <ChevronRight
              size={14}
              className={`transform transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
          </button>
        )}
        {(isExpanded || isCollapsed) && (
          <nav className="space-y-1 mt-1">
            {section.items.map((item) => renderNavItem(item))}
          </nav>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-4 border-b border-gray-200 dark:border-gray-700`}>
        <Link
          to="/admin/dashboard"
          className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}
          onClick={() => setIsMobileOpen(false)}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center shadow-lg">
            <Shield size={20} className="text-white" />
          </div>
          {!isCollapsed && (
            <div>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                Admin
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Control Panel
              </p>
            </div>
          )}
        </Link>

        {/* Desktop collapse button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>

        {/* Mobile close button */}
        <button
          onClick={() => setIsMobileOpen(false)}
          className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4 px-3">
        {navigationSections.map((section) => renderSection(section))}
      </div>

      {/* Footer - User section */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-3">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 mb-2`}>
          <Avatar
            firstName={state.user?.firstName}
            lastName={state.user?.lastName}
            profilePictureUrl={state.user?.profilePictureUrl}
            size="sm"
          />
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {state.user?.firstName} {state.user?.lastName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                Administrator
              </p>
            </div>
          )}
        </div>

        <button
          onClick={handleLogout}
          className={`
            w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}
            px-3 py-2.5 rounded-lg text-sm font-medium
            text-red-600 dark:text-red-400
            hover:bg-red-50 dark:hover:bg-red-900/20
            transition-colors
          `}
          title={isCollapsed ? 'Logout' : undefined}
        >
          <LogOut size={18} />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside
        className={`
          hidden lg:flex lg:flex-col
          fixed top-0 left-0 h-screen z-30
          bg-white dark:bg-gray-800
          border-r border-gray-200 dark:border-gray-700
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        {sidebarContent}
      </aside>

      {/* Sidebar - Mobile */}
      <aside
        className={`
          lg:hidden flex flex-col
          fixed top-0 left-0 h-screen z-50
          bg-white dark:bg-gray-800
          border-r border-gray-200 dark:border-gray-700
          transition-transform duration-300 ease-in-out
          w-72
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent}
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileOpen(true)}
                className="lg:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Menu size={24} />
              </button>

              {/* Breadcrumb or page title could go here */}
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  System Administration
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* System Status Indicator */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs font-medium text-green-700 dark:text-green-400">
                  All Systems Operational
                </span>
              </div>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
              </button>

              {/* Notifications */}
              <NotificationBell />

              {/* User avatar (mobile) */}
              <div className="lg:hidden">
                <Avatar
                  firstName={state.user?.firstName}
                  lastName={state.user?.lastName}
                  profilePictureUrl={state.user?.profilePictureUrl}
                  size="sm"
                />
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
